import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Star, GripVertical } from "lucide-react";
import { apiFetch, resolveImageUrl, API } from "../lib/api";

/** images: [{ url, path, alt }] */
export default function ImageUploader({ images = [], onChange, max = 10 }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const upload = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    if (images.length + list.length > max) {
      toast.error(`Up to ${max} images per product`);
      return;
    }
    setBusy(true);
    const next = [...images];
    for (const f of list) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const res = await fetch(`${API}/uploads/image`, {
          method: "POST", body: fd, credentials: "include",
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || "Upload failed");
        next.push({ url: data.url, path: data.path, alt: f.name });
      } catch (e) {
        toast.error(e.message || "Upload failed");
      }
    }
    setBusy(false);
    onChange(next);
    toast.success(`${list.length} image${list.length > 1 ? "s" : ""} uploaded`);
  };

  const removeAt = (idx) => {
    const next = images.filter((_, i) => i !== idx);
    onChange(next);
  };
  const makePrimary = (idx) => {
    if (idx === 0) return;
    const next = [...images];
    const [item] = next.splice(idx, 1);
    next.unshift(item);
    onChange(next);
  };
  const setAlt = (idx, alt) => {
    const next = images.map((img, i) => (i === idx ? { ...img, alt } : img));
    onChange(next);
  };
  const move = (from, to) => {
    if (to < 0 || to >= images.length) return;
    const next = [...images];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
  };

  return (
    <div className="image-uploader" data-testid="image-uploader">
      <div className="uploader-grid">
        {images.map((img, i) => (
          <div key={(img.path || img.url) + i} className="uploader-tile">
            <img src={resolveImageUrl(img)} alt={img.alt || ""} />
            {i === 0 && <span className="tile-main">Main</span>}
            <div className="tile-actions">
              <button type="button" onClick={() => makePrimary(i)} title="Set as main" aria-label="Set as main"
                      data-testid={`image-primary-${i}`}><Star size={14}/></button>
              <button type="button" onClick={() => move(i, i - 1)} title="Move left" aria-label="Move left">
                <GripVertical size={14}/></button>
              <button type="button" onClick={() => removeAt(i)} title="Remove" aria-label="Remove"
                      data-testid={`image-remove-${i}`}><Trash2 size={14}/></button>
            </div>
            <input className="tile-alt" placeholder="Alt text" value={img.alt || ""}
                   onChange={(e) => setAlt(i, e.target.value)} aria-label="Image alt text"/>
          </div>
        ))}
        <button type="button" className="uploader-add" onClick={() => inputRef.current?.click()}
                disabled={busy} data-testid="upload-image-button">
          <Upload size={18}/>{busy ? "Uploading…" : "Add images"}
        </button>
      </div>
      <input ref={inputRef} type="file" accept="image/*" multiple style={{ display: "none" }}
             onChange={(e) => upload(e.target.files)} data-testid="file-input"/>
      <p className="fine-print">Up to {max} images · WebP/JPG/PNG · max 10 MB each</p>
    </div>
  );
}
